import React, {useEffect} from "react";
import {Button, IconButton, TableCell, Tooltip} from "@mui/material";
import TableRow from "@mui/material/TableRow";
import {arrayMove} from "react-sortable-hoc";
import EnhancedMappingTable from "./EnhancedMappingTable";
import {Add} from "@mui/icons-material";
import EnhancedBulkMappingEditor from "./EnhancedBulkMappingEditor";
import {getAttrOptions} from "../../../../inventory/shared/mapping_templates";
import {FilterHeader} from "../advanced_export";

// region TypeScript Interfaces
interface RuleItem {
    SourceField?: string;
    TargetField?: string;
    Condition?: unknown;
    [key: string]: unknown;
}

interface AttributeOption {
    Name: string;
    FriendlyName: string;
    [key: string]: unknown;
}

interface RuleSelectOption {
    label: string;
    value: string;
    [key: string]: unknown;
}

interface RulesBlockProps {
    type: string;
    attributes: AttributeOption[];
    profile: { Rules: RuleItem[]; Fields?: unknown[]; [key: string]: unknown };
    setProfile: (updater: (prev: any) => any) => void;
    isRetailer?: boolean;
    sourceOptions?: RuleSelectOption[];
    targetOptions?: RuleSelectOption[];
    rowActions?: (item: RuleItem) => React.ReactNode;
}
// endregion

export default function RulesBlock(props: RulesBlockProps) {
    const {type, attributes, profile, setProfile} = props;

    const [bulkAddRulesOpen, setBulkAddRulesOpen] = React.useState(false);

    const [currentTargetOptions, setCurrentTargetOptions] = React.useState<RuleSelectOption[]>([]);
    const [currentSourceOptions, setCurrentSourceOptions] = React.useState<RuleSelectOption[]>([]);

    //region Functions
    const onSortEnd = ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
        setProfile(prevState => (
            {...prevState, Rules: arrayMove(prevState.Rules, oldIndex, newIndex)}
        ))
    }

    const onTargetUpdate = (index: number, value: string) => {
        setProfile(prevState => {
            let newRules = [...prevState.Rules];
            newRules[index].TargetField = value;

            let updatedTargetOptions = getAttrOptions(newRules, currentTargetOptions);
            updatedTargetOptions = updatedTargetOptions.map((option: any) => ({value: option.value, label: option.label}));
            setCurrentTargetOptions(updatedTargetOptions);

            return ({...prevState, Rules: newRules});
        })
    }

    const onSourceUpdate = (index: number, value: string) => {
        setProfile(prevState => {
            let newRules = [...prevState.Rules];
            newRules[index].SourceField = value;
            return ({...prevState, Rules: newRules});
        })
    }

    const onConditionUpdate = (index: number, condition: unknown) => {
        setProfile(prevState => {
            let newItems = [...prevState.Rules];
            newItems[index].Condition = condition;

            let updatedTargetOptions = getAttrOptions(newItems, currentTargetOptions);
            updatedTargetOptions = updatedTargetOptions.map((option: any) => ({value: option.value, label: option.label}));
            setCurrentTargetOptions(updatedTargetOptions);

            return ({...prevState, Rules: newItems});
        });
    }

    const onRuleAdd = () => {
        setProfile(prevState => {
            return ({
                ...prevState,
                Rules: [...prevState.Rules, {TargetField: "", SourceField: ""}]
            });
        })
    }

    const onDeleteRule = (index: number) => {
        setProfile(prevState => {
            let newRules = [...prevState.Rules];
            newRules.splice(index, 1);
            return ({...prevState, Rules: newRules});
        });
    }
    //endregion

    //region Elements
    const headers = (
        <TableRow>
            <TableCell style={{padding: 0, margin: 0}}></TableCell>

            <TableCell style={{padding: 0}}>
                <FilterHeader>
                    Order
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Field
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Maps From
                </FilterHeader>
            </TableCell>

            <TableCell>
                <FilterHeader>
                    Condition
                </FilterHeader>
            </TableCell>

            <TableCell></TableCell>
        </TableRow>
    )

    const actions = (
        <>
            <Button variant="outlined" color="primary"
                    onClick={onRuleAdd}
            >
                + Add Rule
            </Button>

            <Button variant="outlined" color="primary"
                    onClick={() => setBulkAddRulesOpen(true)}
            >
                + Bulk Add Rules
            </Button>
        </>
    )

    const rowActions = (item: RuleItem) => {
        return (
            <>
                <Tooltip title={"Add to export fields"}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setProfile(prevState => {
                                let newFields = [...prevState.Fields];
                                newFields.push({field: item.TargetField, alias: ""});
                                return ({...prevState, Fields: newFields});
                            })
                        }}
                        size="large">
                        <Add/>
                    </IconButton>
                </Tooltip>
            </>
        );
    }
    //endregion

    useEffect(() => {
        switch (type) {
            case "products":
                let productOptions = getAttrOptions(
                    (profile.Rules || []).map((r: any) => ({...r})),
                    attributes.map(attribute => ({label: attribute.FriendlyName, value: attribute.Name}))
                );

                setCurrentSourceOptions(productOptions);
                setCurrentTargetOptions(productOptions);
                break;
            case "supplierProducts":
                let supplierProductOptions = getAttrOptions(
                    (profile.Rules || []).map((r: any) => ({...r})),
                    attributes.map(attribute => ({label: attribute.FriendlyName, value: attribute.Name}))
                );

                setCurrentSourceOptions(supplierProductOptions);
                setCurrentTargetOptions(supplierProductOptions);
                break;
            case "categories":
                let categoriesOptions = ["Id", "ParentId", "Name", "AttributeSet", "DestinationId", "BannerURL", "SyncStatus", "Level", "Children", "Path", "LastModified"];

                setCurrentTargetOptions(categoriesOptions.map(option => ({value: option, label: option})));
                setCurrentSourceOptions(categoriesOptions.map(option => ({value: option, label: option})));
                break;
            case "attributes":
                let attributeOptions = ["Name", "FriendlyName", "Type", "Description", "Options", "Group", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];

                setCurrentTargetOptions(attributeOptions.map(option => ({value: option, label: option})));
                setCurrentSourceOptions(attributeOptions.map(option => ({value: option, label: option})));
                break;
            case "attributeSets":
                let attributeSetOptions = ["Name", "FriendlyName", "Attributes", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];

                setCurrentTargetOptions(attributeSetOptions.map(option => ({value: option, label: option})));
                setCurrentSourceOptions(attributeSetOptions.map(option => ({value: option, label: option})));
                break;
            case "generic":
                let genericOptions = getAttrOptions(
                    (profile.Rules || []).map((r: any) => ({...r})),
                    props.sourceOptions
                );

                setCurrentSourceOptions(genericOptions);
                setCurrentTargetOptions(props.targetOptions || []);
                break;
            default:
                let defaultOptions = getAttrOptions(
                    (profile.Rules || []).map((r: any) => ({...r})),
                    attributes.map(attribute => ({label: attribute.FriendlyName, value: attribute.Name}))
                );

                setCurrentSourceOptions(defaultOptions);
                setCurrentTargetOptions(defaultOptions);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributes, profile]);

    return (
        <>
            <EnhancedMappingTable
                type={"rules"}
                conditionaEditorTitle={"Edit Rule"}

                headers={headers}
                actions={actions}
                rowActions={type !== "generic" ? rowActions : props.rowActions}

                items={profile.Rules}
                targetOptions={currentTargetOptions}
                sourceOptions={currentSourceOptions}

                onSortEnd={onSortEnd}
                onItemDelete={onDeleteRule}
                onTargetUpdate={onTargetUpdate}
                onSourceUpdate={onSourceUpdate}
                onConditionUpdate={onConditionUpdate}
            />

            <EnhancedBulkMappingEditor
                onSave={(rules: any) => {
                    setProfile(prevState => {
                        return ({...prevState, Rules: [...prevState.Rules, ...rules]});
                    });
                }}
                title={"Bulk Add Rules"} type={type}
                open={bulkAddRulesOpen} setOpen={setBulkAddRulesOpen}
                sourceOptions={currentSourceOptions} targetOptions={currentTargetOptions}
            />
        </>
    )
}
